/**
 * The pieces of the `/easy-quote` screen. `../Login.tsx` renders {@link EasyQuoteSteps} in
 * `"easyQuote"` mode; it shows the page title and, in a white card, one step at a time:
 * - `"launcher"`: {@link Launcher}, "Check Out as Guest" or "Log In";
 * - `"easy-quote"`: `EasyQuoteGuestForm.tsx`, which uses {@link EasyQuoteHeader} and
 *   {@link EasyQuoteErrorMessage};
 * - `"login"`: `../LoginHelpers/LoginForm.tsx` with `inEasyQuoteFlow`.
 * The step state is owned by `Login.tsx` (see its header for why it is also in sessionStorage).
 * Copy comes from `EasyQuoteContent.tsx`.
 */
import React from "react";
import { css } from "@emotion/react";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { Alert } from "@ryerson/frontend.notification";
import { EasyQuoteContent, EasyQuoteLanguageText } from "./EasyQuoteContent";
import { useTheme } from "@ryerson/frontend.theme";
import { EmailLink } from "@ryerson/frontend.navigation";
import { Button } from "@ryerson/frontend.button";
import styled from "@emotion/styled";
import { useApplication } from "@ryerson/frontend.application";
import { DisplaySteps } from "./EasyQuote.types";
import LoginForm from "../LoginHelpers/LoginForm";
import { EasyQuoteGuestForm } from "./EasyQuoteGuestForm";

// The "or" rule between the launcher's two buttons. `with: 50px` is not a CSS property, so the
// browser ignores that declaration.
const DividerWithCenterText = styled(Flex)`
	height: 0.8px;

	${(props: any) => {
		const { theme, isMobile = false } = props;
		return css`
			div {
				opacity: 0.2;
				height: 0.5px;
				width: 150%;
				border-top: 0.5px solid ${theme.colors.primary.gray};
			}
			span {
				font-family: "Roboto";
				background-color: ${theme.colors.primary.white};
				padding: 0px 10px;
				position: relative;
				top: -11px;
				with: 50px;
			}

			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-bottom: 0px;
				margin-top: 0px;
			}
		`;
	}}
`;
interface EasyQuoteHeaderProps {
	content: EasyQuoteLanguageText;
	isMobile: boolean;
}

/**
 * The first step: two buttons that set the step to `"easy-quote"` (guest) or `"login"`. Both
 * buttons carry `dataTestId="easy-quote-submit-button"`, as does the guest form's submit button.
 */
export const Launcher: React.FC<
	EasyQuoteHeaderProps & {
		setDisplayStep: React.Dispatch<React.SetStateAction<"login" | "launcher" | "easy-quote">>;
	}
> = ({ content, isMobile, setDisplayStep }) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	return (
		<Flex
			className="outer-buttons-container"
			css={{
				width: isMobile ? "90%" : "65%",
				gap: isMobile ? 34 : 37,
				minHeight: "200px",
				maxWidth: "400px",
			}}
			direction="column"
			justifyContent="space-evenly"
		>
			<FlexItem
				className="inner-button-column"
				css={{
					width: language === "fr" ? "288px" : "239px",
					alignSelf: "center",
				}}
			>
				<Button
					fullwidth={true}
					dataTestId="easy-quote-submit-button"
					label={content.checkout}
					type="primary"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setDisplayStep("easy-quote");
					}}
				/>
			</FlexItem>

			<DividerWithCenterText {...{ theme, isMobile }}>
				<div />
				<span>{content.or}</span>
				<div />
			</DividerWithCenterText>

			<FlexItem css={{ width: language === "fr" ? "288px" : "239px", alignSelf: "center" }}>
				<Button
					fullwidth={true}
					dataTestId="easy-quote-submit-button"
					label={content.login}
					type="primary"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setDisplayStep("login");
					}}
				/>
			</FlexItem>
		</Flex>
	);
};

/** The page heading above the card, "Ready to Order Your Quote?", in white. */
export const EasyQuotePageTitle: React.FC<EasyQuoteHeaderProps> = ({ content, isMobile }) => {
	return (
		<Typography
			className="easy-quote-guest-form-header"
			variant={isMobile ? "h2" : "h1"}
			type="tertiary"
			css={{
				fontFamily: "Poppins",
				color: "#FFFFFF",
				margin: isMobile ? "0px 0px 33px 0px" : "10px  0px 75px 0px",
				lineHeight: isMobile ? "45px!important" : "56px!important",
				fontSize: isMobile ? "37px!important" : "55px!important",
				fontWeight: "200!important",
			}}
		>
			{content.easyQuotePageTitle}
		</Typography>
	);
};

/** The guest form's heading, "Check out as Guest"; tighter bottom margin while an error shows. */
export const EasyQuoteHeader: React.FC<EasyQuoteHeaderProps & { hasPageError: boolean }> = ({
	content,
	isMobile,
	hasPageError,
}) => {
	return (
		<Flex justifyContent="center" css={{ width: "100%" }}>
			<Typography
				className="easy-quote-guest-form-header"
				variant={isMobile ? "h3" : "h4"}
				type="primary"
				css={{
					margin: isMobile
						? `0px 0px ${hasPageError ? "0px" : "20px"} 0px`
						: `0px  0px ${hasPageError ? "0px" : "34px"} 0px`,
				}}
			>
				{content.checkoutAsGuestButton}
			</Typography>
		</Flex>
	);
};

interface EasyQuoteErrorMessageProps {
	hasPageError: boolean;
	errorType: "validationError" | "apiError" | null;
	content: EasyQuoteLanguageText;
	correlationId?: string;
	isMobile?: boolean;
}

/**
 * The guest form's error. A `"validationError"` shows the "review the highlighted fields"
 * message. An `"apiError"` shows one message for every refusal of the session request, with the
 * brand's email address (`getBrandInfo().email`) and, when present, the request id from the error
 * body (`correlationId`), below at half opacity. Renders nothing unless `hasPageError`
 * is true and `errorType` is set.
 */
export const EasyQuoteErrorMessage: React.FC<EasyQuoteErrorMessageProps> = ({
	hasPageError,
	errorType,
	content,
	correlationId,
	isMobile = false,
}) => {
	const { theme } = useTheme();
	const { white } = theme.colors.primary;
	if (hasPageError !== true || !errorType) {
		return null;
	}
	return (
		<div
			id="easy-quote-error-message"
			className="easy-quote-error-message"
			style={{ marginTop: "20px", marginBottom: "20px" }}
		>
			<Alert severity="error" size="sm" outline={true}>
				<Flex direction="column" css={{ gap: "8px", alignItems: "center" }}>
					<Typography type="primary" size="md" autoResize={false}>
						{errorType === "validationError"
							? content.validationErrorMessage
							: content.genericErrorMessage}

						{errorType === "apiError" && (
							<Typography size="md" type="primary">
								<EmailLink to={content.brandedEmail}>
									{content.brandedEmail}
								</EmailLink>
								{content.genericErrorMessage2}
							</Typography>
						)}

						{correlationId && errorType === "apiError" && (
							<Typography
								css={{ marginTop: 12, opacity: ".5" }}
								variant="div"
								size="md"
								type="primary"
							>
								{correlationId}
							</Typography>
						)}
					</Typography>
				</Flex>
			</Alert>
		</div>
	);
};

const checkIcon = css`
	margin-right: 20px;
	align-self: center;
`;

/**
 * The `/easy-quote` screen: title and the current step's card (see the file header).
 * `successMessage` goes to the sign-in step's info alert. `hasMounted` is set but not read.
 */
export const EasyQuoteSteps: React.FC<{
	isMobile: boolean;
	successMessage: string;
	displayStep: DisplaySteps;
	setDisplayStep: React.Dispatch<React.SetStateAction<DisplaySteps>>;
}> = ({ isMobile, successMessage, displayStep, setDisplayStep }) => {
	const [hasMounted, setHasMounted] = React.useState(false);
	React.useEffect(() => {
		setHasMounted(true);
	}, []);

	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	const content = EasyQuoteContent[language] || EasyQuoteContent.en;

	return (
		<Flex
			className="easy-quote-steps-container"
			direction="column"
			alignItems="center"
			justifyContent="center"
			css={{
				paddingBottom: "140px",
				padding: isMobile ? "10px 20px" : "55px 40px",
				width: "100%",
				height: "100%",
			}}
		>
			<>
				<EasyQuotePageTitle content={content} isMobile={isMobile} />

				<Flex
					className="white-box-form-container"
					alignItems="center"
					justifyContent="center"
					css={{
						backgroundColor: theme.colors.primary.white,
						width: "100%",
						borderRadius: "10px",
						maxWidth: isMobile ? "unset" : "680px",
						minWidth: isMobile ? "300px" : "inherit",
						height: "max-content",
						padding: "40px",
					}}
				>
					{displayStep === "launcher" && (
						<Launcher
							content={content}
							isMobile={isMobile}
							setDisplayStep={setDisplayStep}
						/>
					)}

					{displayStep === "easy-quote" && (
						<EasyQuoteGuestForm isMobile={isMobile} setDisplayStep={setDisplayStep} />
					)}

					{displayStep === "login" && (
						<LoginForm
							isMobile={isMobile}
							message={successMessage}
							inEasyQuoteFlow={true}
							setDisplayStep={setDisplayStep}
						/>
					)}
				</Flex>
			</>
		</Flex>
	);
};
